<template>
    <div>
        <slot
            v-bind="{
                ...$data,
                itemsTotal,
                itemsCount,
                pageLimit,
                totalPages,
                filteredItems,
                controlsChange,
                sortOptions,
                paginationShowNav,
                setPageNumber,
                pageNum,
            }"
        />
    </div>
</template>

<script>
import {
    orderBy,
    size,
    slice,
    isEmpty,
} from 'lodash';
import {
    getInitialFilters,
    phraseFilter,
} from '@/utils/listFilters';
import dataChangeHandler from '@/utils/dataChangeHandler';

export default {
    props: {
        items: {
            type: [Object, Array],
            required: true,
        },
        sortOptions: {
            type: Array,
            default: () => [],
        },
        paginate: {
            type: Boolean,
            default: false,
        },
        pageLimit: {
            type: Number,
            default: 5,
        },
        disableFilters: {
            type: Boolean,
            default: false,
        },
        disableFiltersItemsLimit: {
            type: Number,
            default: 12,
        },
        initialFiltersConfig: {
            type: Object,
            default: () => ({}),
        },
        filterFunction: {
            type: Function,
            default: (filters, items) => items,
        },
    },

    data: () => ({
        itemsCount: 0,
        layoutType: 'grid',
        sortBy: '',
        iteratees: null,
        interatees: '',
        searchPhrase: '',
        filters: [],
        isReverse: false,
        pageNum: 1,
    }),

    computed: {
        /**
         * Count total items
         *
         * @returns  {number}  Number of all items
         */
        itemsTotal() {
            return size(this.items);
        },

        /**
         * @returns {Array} Filtered, sorted items
         */
        filteredItems() {
            let items = this.items;

            if (
                this.disableFilters &&
                (0 === this.itemsTotal || this.disableFiltersItemsLimit > this.itemsTotal)
            ) {
                return items;
            }

            const order = this.isReverse ? 'asc' : 'desc';
            const orders = this.iteratees
                ? Array(this.iteratees.length).fill(order)
                : order;

            items = orderBy(items, this.iteratees || this.sortBy, orders);

            items = ('' !== this.searchPhrase)
                ? phraseFilter(items, ['title', 'description', 'summary', 'artistGuest', 'tags'], this.searchPhrase)
                : items;

            items = this.filterFunction(this.filters, items);

            this.setItemsCount(items);

            if (! this.paginate) {
                return items;
            }

            if (this.pageLimit < this.itemsTotal) {
                const offset = (this.pageNum - 1) * this.pageLimit;

                return slice(items, offset, this.pageLimit * this.pageNum);
            } else {
                return items;
            }
        },

        /**
         * @returns {number} Total number of pages
         */
        totalPages() {
            return Math.ceil(this.itemsCount / this.pageLimit);
        },

        /**
         * Pagination arrow navigation display control
         *
         * @returns {boolean} Whether show prev/next buttons
         */
        paginationShowNav() {
            return 3 < this.totalPages;
        },
    },

    watch: {
        items: {
            immediate: true,

            /**
             * Set status filters and watch for items change
             *
             * @param {object} items items array.
             */
            handler(items) {
                this.filters = ! isEmpty(this.initialFiltersConfig)
                    ? getInitialFilters(items, this.initialFiltersConfig)
                    : [];
            },
        },
    },

    /**
     * Mounted hook
     */
    mounted() {
        if (0 !== this.sortOptions.length) {
            this.sortBy = this.sortOptions[0].value;
            this.iteratees = this.sortOptions[0].iteratees || null;
            this.isReverse = this.sortOptions[0]?.isReverse ?? false;
        }
    },

    methods: {
        /**
         * Handle controls change
         *
         * @param   {object}  changed  Changed configuation
         *
         */
        controlsChange(changed) {
            this.pageNum = 1;

            dataChangeHandler(this, changed);
        },

        /**
         * Set items count
         *
         * @param   {Array}  items  Items array
         *
         */
        setItemsCount(items) {
            this.itemsCount = size(items);
        },

        /**
         * Sets page number
         *
         * @param {number} number Page number
         */
        setPageNumber(number) {
            this.pageNum = number;
        },
    },
};
</script>
